import * as React from "react";
import { cx } from "utils/join-classnames";
import {
    Modal,
    Button,
    Icon,
    TagInput,
    Spinner,
    Dropdown,
    CheckBox,
    VideoPlayer,
    FormElement,
} from "ui/components";
import { Fragment, VideoActionRequest } from "core/models";
import { UpdateFragment, UploadManager } from "core/services";
import { useSessionContext } from "ui/context/SessionContext";
import { usePublishContext } from "ui/context/PublishContext";
import { useFormValidation } from "ui/hooks/use-form-validation";
import Configuration from "Configuration";
import useKijk from "ui/hooks/use-kijk";
import styles from "./MetadataModal.module.scss";

interface Props {
    handleClose(): void;
    onSave(changedProps: UpdateFragment): void;
    handleNewThumbnail(thumbId: number): void;
    fragment: Fragment;
}

const EditModal: React.FC<Props> = ({
    handleClose,
    onSave,
    fragment,
    handleNewThumbnail,
}: Props) => {
    const [clipTitle, setClipTitle] = React.useState<string>(fragment.title);
    const [clipDescription, setClipDescription] = React.useState<string>(
        fragment.description,
    );
    const [clipTags, setClipTags] = React.useState<string[]>(fragment.tags);
    const [thumbnail, setThumbnail] = React.useState<string>(
        fragment.getThumbnailURL(),
    );
    const [thumbnailpicker, setThumbnailpicker] =
        React.useState<boolean>(false);
    const [uploadedThumb, setUploadedThumb] = React.useState<number>();
    const [thumbnailPending, setThumbnailPending] =
        React.useState<boolean>(false);
    const [showTalpaKijk, setTalpaKijk] = React.useState<string | undefined>();
    const [, setScreenshot] = React.useState<string | null>(null);
    const [videoPlayerRequest, setVideoPlayerRequest] =
        React.useState<VideoActionRequest>({ type: "none" });
    const [useScreenshot, setUseScreenshot] = React.useState<
        "use" | "download" | null
    >(null);

    const hiddenFileInput =
        React.useRef() as React.MutableRefObject<HTMLInputElement>;
    const sessionContext = useSessionContext();
    const publishContext = usePublishContext();
    const {
        handleSelectGenre,
        handleSelectEpisode,
        handleChangeOnlySeries,
        handleChangeExclusive,
        handleDescription,
        searchEpisodes,
        exportProperties,
        genres,
        episodes,
        selectedGenre,
        selectedEpisode,
        exclusive,
        onlySeries,
        episodesPending,
        description,
    } = useKijk(showTalpaKijk, fragment.properties);

    const handleDownloadFrame = async () => {
        setThumbnailPending(true);
        setVideoPlayerRequest({ type: "getScreenshot" });
        setUseScreenshot("download");
    };
    const handleDownloadThumbnail = () => {
        if (thumbnail.search("blob") !== -1) {
            const a = document.createElement("a");
            a.href = thumbnail;
            a.download = fragment.title;
            a.click();
        } else {
            window.open(thumbnail, "_blank");
        }
    };
    const handleNewScreenshot = async (newScreenshot: string) => {
        if (!sessionContext.session || !sessionContext.selectedProject) {
            return;
        }
        setScreenshot(newScreenshot);
        if (useScreenshot === "use") {
            setUseScreenshot(null);
            setThumbnail(newScreenshot);
            setThumbnailpicker(false);
            const blob = await fetch(newScreenshot).then((x) => x.blob());
            const file = new File([blob], "thumb.png");
            const uploadManager = new UploadManager(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
            );
            const uploadThumbnail = await uploadManager.uploadThumbnail(
                file,
                () => {
                    // We could manage the thumbnail upload progress here, Math.ceil(val*100)
                },
            );
            if (uploadThumbnail.error || !uploadThumbnail.value) {
                console.error(uploadThumbnail.error);
                return;
            }
            setThumbnailPending(false);
            setUploadedThumb(uploadThumbnail.value.id);
        } else if (useScreenshot === "download") {
            setUseScreenshot(null);
            const a = document.createElement("a");
            a.href = newScreenshot;
            a.download = fragment.title;
            a.click();
            setThumbnailPending(false);
        }
    };

    const handleUseThumbnail = async () => {
        setThumbnailPending(true);
        setVideoPlayerRequest({ type: "getScreenshot" });
        setUseScreenshot("use");
    };

    const uploadThumb = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!sessionContext.session || !sessionContext.selectedProject) {
            return;
        }
        const fileApiUrl = Configuration.get("fileApiURL");
        setThumbnailPending(true);
        if (!event.target.files || !("length" in event.target.files)) {
            return;
        }
        const fileUploaded = event.target.files[0];
        const uploadManager = new UploadManager(
            sessionContext.session.token,
            sessionContext.selectedProject.id,
        );
        const uploadThumbnail = await uploadManager.uploadThumbnail(
            fileUploaded,
            () => {
                // We could manage the thumbnail upload progress here, Math.ceil(val*100)
            },
        );
        if (uploadThumbnail.error || !uploadThumbnail.value) {
            return;
        }
        setUploadedThumb(uploadThumbnail.value.id);
        setThumbnail(
            `${fileApiUrl}/api/file/read/v1/t/p/${uploadThumbnail.value.token}/${uploadThumbnail.value.path}`,
        );
        setThumbnailPending(false);
    };
    const handleSave = () => {
        const changedProps: UpdateFragment = {
            id: fragment.id,
            title: clipTitle,
            tags: clipTags,
            description: clipDescription,
        };
        if (showTalpaKijk) {
            changedProps.properties = exportProperties();
        }
        onSave(changedProps);
        if (uploadedThumb) {
            handleNewThumbnail(uploadedThumb);
        }
    };

    React.useEffect(() => {
        const kijkIntegration = publishContext.accounts.find(
            (account) => account.publisherkey === "custom_talpakijkv2",
        );
        if (kijkIntegration) {
            setTalpaKijk(kijkIntegration.key);
        }
    }, [publishContext.accounts]);

    const form = useFormValidation({
        title: {
            valid: false,
            touched: false,
            value: fragment.title,
            errorMsg: "",
            rules: {
                required: true,
                minLength: 2,
            },
        },
        description: {
            valid: true,
            touched: true,
            value: fragment.description || null || undefined,
            errorMsg: "",
            title: "Description",
            type: "textarea",
            rules: {
                required: true,
                minLength: 2,
                maxLength: 750,
            },
        },
    });

    const kijkJSX = (
        <div className={styles.kijk}>
            <div className={styles.kijk__heading}>
                <Icon name="kijk" /> <span>Kijk Metadata</span>
            </div>
            <div className={styles.formelement}>
                <CheckBox
                    title="Only search for series"
                    theme="dark"
                    id="onlySeries"
                    value={onlySeries.toString()}
                    onChange={handleChangeOnlySeries}
                    className={styles.kijk__checkbox}
                />
                <CheckBox
                    title="Exclusive"
                    theme="dark"
                    id="exclusive"
                    value={exclusive.toString()}
                    onChange={handleChangeExclusive}
                    className={styles.kijk__checkbox}
                />
            </div>
            <div className={styles.formelement}>
                <div className={styles.formelement__caption}>
                    Episode or series
                </div>
                <Dropdown
                    theme="dark"
                    onValueChanged={handleSelectEpisode}
                    value={selectedEpisode || ""}
                    options={episodes}
                    searchable
                    onSearch={searchEpisodes}
                    pending={episodesPending}
                    placeholderText="Select episode or series"
                    fullWidth
                />
            </div>
            <div className={styles.formelement}>
                <div className={styles.formelement__caption}>Genre</div>
                <Dropdown
                    theme="dark"
                    onValueChanged={handleSelectGenre}
                    value={selectedGenre || ""}
                    placeholderText="Select genre"
                    options={genres}
                />
            </div>
            <div className={styles.formelement__description}>
                <FormElement
                    defaultText="Add a description for this clip"
                    onChange={(value) => {
                        form.inputChangeHandler("description", value);
                        setClipDescription(value?.toString() || "");
                        handleDescription(value?.toString() || "");
                    }}
                    fieldState={form.formState.description}
                    theme="dark"
                    type="textarea"
                    id="description"
                    title="Description"
                    className={styles.kijk__description}
                    required
                />
            </div>
        </div>
    );

    const modalContent = (
        <div className={cx(styles.container)}>
            {thumbnailpicker && (
                <div className={cx(styles.thumbnailpicker)}>
                    <VideoPlayer
                        type="video"
                        videoSrc={fragment.getMediaURL()}
                        onScreenshot={handleNewScreenshot}
                        actionRequest={videoPlayerRequest}
                    />
                </div>
            )}
            {!thumbnailpicker && (
                <div className={styles.form}>
                    <div className={styles.column__left}>
                        <div className={styles.formelement}>
                            <FormElement
                                theme="dark"
                                defaultText="Name your clip"
                                type="text"
                                title="Title"
                                id="title"
                                fieldState={form.formState.title}
                                onChange={(value) => {
                                    form.inputChangeHandler("title", value);
                                    setClipTitle(value?.toString() || "");
                                }}
                                required
                            />
                        </div>
                        <div className={styles.formelement}>
                            <div className={styles.formelement__caption}>
                                Tags
                            </div>
                            <TagInput
                                value={clipTags}
                                onChange={(value) => {
                                    setClipTags(value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={cx(styles.column__right)}>
                        <div className={styles.formelement}>
                            <div className={styles.formelement__caption}>
                                Thumbnail
                            </div>
                            <div
                                className={styles.thumbnail}
                                style={{ backgroundImage: `url(${thumbnail})` }}
                            >
                                <div
                                    className={styles.thumbnail__download}
                                    onClick={handleDownloadThumbnail}
                                    role="presentation"
                                >
                                    <Icon name="download" />
                                </div>
                                {thumbnailPending && (
                                    <div className={styles.spinner}>
                                        <Spinner />
                                    </div>
                                )}
                            </div>
                            <div className={styles.btnbar}>
                                <div>
                                    <Button
                                        type="secondary"
                                        theme="light"
                                        icon="picture"
                                        onClick={() => setThumbnailpicker(true)}
                                    >
                                        Take other
                                    </Button>
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={hiddenFileInput}
                                        onChange={uploadThumb}
                                        style={{ display: "none" }}
                                    />
                                    <Button
                                        type="secondary"
                                        theme="light"
                                        icon="upload"
                                        onClick={() =>
                                            hiddenFileInput.current.click()
                                        }
                                    >
                                        Upload
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showTalpaKijk && !thumbnailpicker && kijkJSX}
        </div>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="amber"
            theme="dark"
            onClose={
                thumbnailpicker ? () => setThumbnailpicker(false) : handleClose
            }
            onSave={thumbnailpicker ? handleUseThumbnail : handleSave}
            saveButtonText={thumbnailpicker ? "Use thumbnail" : "Save"}
            saveButtonDisabled={
                thumbnailPending ||
                !form.formValid ||
                (!!showTalpaKijk && !description)
            }
            title={
                thumbnailpicker
                    ? "Take other thumbnail"
                    : `Edit metadata for "${fragment.title}"`
            }
            icon="edit"
            extraButton={
                thumbnailpicker ? (
                    <Button
                        type="secondary"
                        theme="light"
                        onClick={handleDownloadFrame}
                    >
                        Download frame
                    </Button>
                ) : undefined
            }
        />
    );
};

export default EditModal;
